import { Autocomplete, Avatar, Stack, TextField, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import astrocartographyStyles from "../Astrocartography.styles";

interface AstroProfileFieldProps {
  profiles: any[];
  selectedProfile: any;
  onProfileChange: (profile: any) => void;
  isLoading: boolean;
  /** Birth coordinates of the loaded chart, shown once it arrives. */
  birthPlace?: { lat: number; lon: number };
}

/**
 * Answers "whose map am I exploring?". The Autocomplete itself is the same one
 * every other admin screen uses — only its surroundings are feature-specific.
 */
const AstroProfileField = (props: AstroProfileFieldProps) => {
  const classes = astrocartographyStyles;
  const { t } = useTranslation();
  const { profiles, selectedProfile, onProfileChange, isLoading, birthPlace } =
    props;

  const name: string = selectedProfile?.name || "";

  const getCoordinates = () => {
    if (!birthPlace) return t("astroChartReady");
    const lat = `${Math.abs(birthPlace.lat).toFixed(2)}° ${birthPlace.lat >= 0 ? "N" : "S"}`;
    const lon = `${Math.abs(birthPlace.lon).toFixed(2)}° ${birthPlace.lon >= 0 ? "E" : "W"}`;
    return `${lat} · ${lon}`;
  };

  return (
    <Stack spacing={1.5}>
      <Typography variant="overline" color="text.secondary">
        {t("astroProfileCaption")}
      </Typography>

      {selectedProfile && (
        <Stack direction="row" spacing={1.25} alignItems="center">
          <Avatar sx={classes.profileAvatar}>
            {name.trim().charAt(0).toUpperCase() || "✦"}
          </Avatar>
          <Stack sx={{ minWidth: 0 }}>
            <Typography variant="subtitle2" noWrap>
              {name}
            </Typography>
            <Typography variant="caption" noWrap>
              {getCoordinates()}
            </Typography>
          </Stack>
        </Stack>
      )}

      <Autocomplete
        fullWidth
        size="small"
        options={profiles || []}
        value={selectedProfile}
        loading={isLoading}
        getOptionLabel={(option: any) => option?.name || ""}
        isOptionEqualToValue={(option: any, value: any) =>
          option?.profileId === value?.profileId
        }
        renderInput={(params) => (
          <TextField {...params} placeholder={t("searchProfile")} />
        )}
        onChange={(event, value) => onProfileChange(value || null)}
      />
    </Stack>
  );
};

export default AstroProfileField;
